import { defineStore } from 'pinia'
import { FabricObject } from 'fabric'
import { discountTypes, discountVariations } from '@/constants/canvas'
interface EnhancedFabricObject extends FabricObject {
  layerName?: string
  fontSize?: number
  textLines?: string[]
  maxLines?: number
  text?: string
  _objects: any
}
interface CanvasState {
  fabricCanvas: any
  canvasJson: any | null
  layers: Record<string, EnhancedFabricObject>
  shouldUpdateCanvas: boolean
  bgImageUrl: string
  canvasLoader: boolean
  canvasError: boolean
  hideLoader: boolean
  snackbarMessage: string
  snackbarColor: string
  showSnackbar: boolean
  enableResetCanvas: boolean
  logoUrl: string
  baseGeneratedCanvasJson: any | null
  baseBackgroundImageUrl: string
  callReset: boolean
  callNext: boolean
  rawCouponCode: string
  discountType: string
  discountVariation: string
  discountValue: string
  baseRawCouponCode: string
  isTextValidationFailed: boolean
  counter: number
  textColor: string
  lastUsedDiscountInfo: {
    type: string
    variation: string
    value: string
    couponCode: string
  }
  baseDiscountInfo: {
    type: string
    variation: string
    value: string
    couponCode: string
  }
  discountInput: {
    type: string
    variation: string
    value: string
    couponCode: string
  }
  originalCanvasJson: any | null
  backgroundImageUrl?: string
  aiGeneratedHeadlines: string[]
  aiGeneratedSubHeadlines: string[]
  areAllEdibleProducts: boolean
}

export const useCanvasStore = defineStore('canvas', {
  state: (): CanvasState => ({
    fabricCanvas: null,
    canvasJson: null,
    layers: {},
    shouldUpdateCanvas: false,
    bgImageUrl: '',
    canvasLoader: false,
    canvasError: false,
    hideLoader: false,
    snackbarMessage: '',
    snackbarColor: '',
    showSnackbar: false,
    enableResetCanvas: false,
    logoUrl: '',
    baseGeneratedCanvasJson: null,
    baseBackgroundImageUrl: '',
    callReset: false,
    callNext: false,
    rawCouponCode: '',
    discountType: discountTypes.none,
    discountVariation: discountVariations.upto,
    discountValue: '',
    baseRawCouponCode: '',
    isTextValidationFailed: false,
    counter: 0,
    textColor: '#000000',
    lastUsedDiscountInfo: {
      type: discountTypes.none,
      variation: discountVariations.upto,
      value: '',
      couponCode: '',
    },
    baseDiscountInfo: {
      type: discountTypes.none,
      variation: discountVariations.upto,
      value: '',
      couponCode: '',
    },
    discountInput: {
      type: discountTypes.none,
      variation: discountVariations.upto,
      value: '',
      couponCode: '',
    },
    originalCanvasJson: null,
    backgroundImageUrl: undefined,
    aiGeneratedHeadlines: [],
    aiGeneratedSubHeadlines: [],
    areAllEdibleProducts: false,
  }),
  actions: {
    setFabricCanvas(fabricCanvas: any) {
      this.fabricCanvas = fabricCanvas
    },
    setCanvasJson(canvasJson: any) {
      this.canvasJson = canvasJson
    },
    setLayers(layers: Record<string, EnhancedFabricObject>) {
      this.layers = layers
    },
    setShouldUpdateCanvas(shouldUpdateCanvas: boolean) {
      this.shouldUpdateCanvas = shouldUpdateCanvas
    },
    setBgImageUrl(bgImageUrl: string) {
      this.bgImageUrl = bgImageUrl
    },
    setCanvasLoader(value: boolean) {
      this.canvasLoader = value
    },
    setCanvasError(value: boolean) {
      this.canvasError = value
    },
    setHideLoader(value: boolean) {
      this.hideLoader = value
    },
    showSnackbarMessage(message: string, color: 'success' | 'error' = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.showSnackbar = true

      setTimeout(() => {
        this.showSnackbar = false
        this.snackbarMessage = ''
      }, 3000)
    },
    setEnableResetCanvas(
      value: boolean,
      baseGeneratedCanvasJson: any,
      baseBackgroundImageUrl: string,
    ) {
      this.enableResetCanvas = value
      this.baseGeneratedCanvasJson = baseGeneratedCanvasJson
      this.baseBackgroundImageUrl = baseBackgroundImageUrl
    },
    setLogoUrl(logoUrl: string) {
      this.logoUrl = logoUrl
    },
    setCallReset() {
      this.callReset = !this.callReset
    },
    setCallNext() {
      this.callNext = !this.callNext
    },
    resetCanvasState() {
      this.canvasJson = null
      this.layers = {}
      this.shouldUpdateCanvas = false
      this.bgImageUrl = ''
      this.canvasLoader = false
      this.canvasError = false
      this.hideLoader = false
      this.snackbarMessage = ''
      this.snackbarColor = ''
      this.showSnackbar = false
      this.enableResetCanvas = false
      this.logoUrl = ''
      this.baseGeneratedCanvasJson = null
      this.baseBackgroundImageUrl = ''
      this.rawCouponCode = this.baseDiscountInfo.couponCode || ''
      this.discountType = this.baseDiscountInfo.type || discountTypes.none
      this.discountVariation = this.baseDiscountInfo.variation || discountVariations.upto
      this.discountValue = this.baseDiscountInfo.value || ''
      this.callReset = false
      this.callNext = false
      this.counter = 0
      this.aiGeneratedHeadlines = []
      this.aiGeneratedSubHeadlines = []
      this.areAllEdibleProducts = false
    },
    resetDiscountInfo() {
      this.rawCouponCode = ''
      this.discountType = discountTypes.none
      this.discountVariation = discountVariations.upto
      this.discountValue = ''
      this.baseRawCouponCode = ''
      this.lastUsedDiscountInfo = {
        type: discountTypes.none,
        variation: discountVariations.upto,
        value: '',
        couponCode: '',
      }
      this.baseDiscountInfo = {
        type: discountTypes.none,
        variation: discountVariations.upto,
        value: '',
        couponCode: '',
      }
    },
    setRawCouponCode(value: string) {
      this.rawCouponCode = value
    },
    setIsTextValidationFailed(value: boolean) {
      this.isTextValidationFailed = value
    },
    setBaseRawCouponCode(value: string) {
      this.baseRawCouponCode = value
    },
    incrementCounter() {
      this.counter++
    },
    setTextColor(color: string) {
      this.textColor = color
    },
    setDiscountType(value: string) {
      this.discountType = value
    },
    setDiscountVariation(value: string) {
      this.discountVariation = value
    },
    setDiscountValue(value: string) {
      this.discountValue = value
    },
    setLastUsedDiscountInfo(discountInfo: {
      type: string
      variation: string
      value: string
      couponCode: string
    }) {
      this.lastUsedDiscountInfo = discountInfo
    },
    setBaseDiscountInfo(discountInfo: {
      type: string
      variation: string
      value: string
      couponCode: string
    }) {
      this.baseDiscountInfo = discountInfo
    },
    setOriginalCanvasJson(canvasJson: any) {
      this.originalCanvasJson = canvasJson
    },
    setBackgroundImageUrl(url: string) {
      this.backgroundImageUrl = url
    },
    setAIGeneratedHeadlines(headlines: string[]) {
      this.aiGeneratedHeadlines = headlines
    },
    setAIGeneratedSubHeadlines(subHeadlines: string[]) {
      this.aiGeneratedSubHeadlines = subHeadlines
    },
    setDiscountInput(discountInfo: {
      type: string
      variation: string
      value: string
      couponCode: string
    }) {
      this.discountInput = discountInfo
    },
    setAreAllEdibleProducts(value: boolean) {
      this.areAllEdibleProducts = value
    }
  },
})
